import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch } from 'react-redux';
import {
  fetchStoneTypes,
  deleteStoneType,
  setStoneTypeEdit,
  clearStoneTypeEdit,
} from '../../../../redux/actions/masters/stoneAttribute/stoneType/stoneTypeAction';
import { showConfirm, showInfo } from '../../../../utills/sweetAlert';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';

const cardStyle = {
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
  minWidth: 0,
  marginBottom: 0,
  fontFamily: 'Poppins, sans-serif',
};
const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
};
const titleStyle = {
  fontWeight: 700,
  fontSize: 18,
  color: '#111',
  letterSpacing: 0.7,
};
const newBtnStyle = {
  background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 26px',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  marginRight: 12,
  letterSpacing: 0.5,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 200, 83, 0.3)',
};
const searchContainer = {
  position: 'relative',
  minWidth: 200,
  marginLeft: 8,
};
const searchInput = {
  width: 200,
  padding: '10px 36px 10px 38px',
  border: '1.5px solid #C8E6C9',
  borderRadius: 8,
  fontSize: 16,
  color: '#1B5E20',
  background: '#fff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border 0.3s',
};
const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#00C853',
  fontSize: 20,
  pointerEvents: 'none',
};
const actionStyle = {
  cursor: 'pointer',
  marginRight: 10,
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: 4,
  color: '#757575',
  transition: 'all 0.3s ease',
};

const StoneTypeTable = ({ onNew, stoneTypes = [], onEdit, onDelete, loading }) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [hoveredAction, setHoveredAction] = useState({});

  useEffect(() => {
    dispatch(fetchStoneTypes());
  }, [dispatch]);

  const handleEdit = async (stoneType) => {
    await showInfo('Edit Stone Type', `Editing stone type: ${stoneType.stone_type_name}`);
    dispatch(setStoneTypeEdit(stoneType));
    if (typeof onEdit === 'function') onEdit(stoneType);
  };

  const handleDelete = async (id, name) => {
    const confirmed = await showConfirm('Trash Stone Type', `Are you sure you want to trash stone type: ${name}?`, 'Trash', 'Cancel');
    if (confirmed) {
      dispatch(deleteStoneType(id));
      if (typeof onDelete === 'function') onDelete(id);
    }
  };

  const handleNew = () => {
    dispatch(clearStoneTypeEdit());
    if (typeof onNew === 'function') onNew();
  };

  const filteredStoneTypes = (stoneTypes || []).filter(st =>
    st.stone_type_name && st.stone_type_name.toLowerCase().includes(search.toLowerCase()) && st.trash === 0
  );

  const columns = [
    {
      name: 'Stonetype Code',
      selector: row => row.stone_type_code,
      sortable: true,
      // width: '110px',
    },
    {
      name: 'Stonetype Name',
      selector: row => row.stone_type_name,
      sortable: true,
      width:'200px',
    },
    {
      name: 'Stone Clarity',
      selector: row => row.stone_clarity ? 'Yes' : 'No',
      sortable: true,
      width: '150px',
      cell: row => (
        <span style={{ 
          color: row.stone_clarity ? '#00C853' : '#757575', 
          fontWeight: 600,
          fontSize: '13px'
        }}>
          {row.stone_clarity ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Stone Color',
      selector: row => row.stone_color ? 'Yes' : 'No',
      sortable: true,
      width: '150px',
      cell: row => (
        <span style={{ 
          color: row.stone_color ? '#00C853' : '#757575', 
          fontWeight: 600,
          fontSize: '13px'
        }}>
          {row.stone_color ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Stone Cut',
      selector: row => row.stone_cut ? 'Yes' : 'No',
      sortable: true,
      width: '150px',
      cell: row => (
        <span style={{ 
          color: row.stone_cut ? '#00C853' : '#757575', 
          fontWeight: 600,
          fontSize: '13px'
        }}>
          {row.stone_cut ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Stone Quality',
      selector: row => row.stone_quality ? 'Yes' : 'No',
      sortable: true,
      width: '150px',
      cell: row => (
        <span style={{ 
          color: row.stone_quality ? '#00C853' : '#757575', 
          fontWeight: 600,
          fontSize: '13px'
        }}>
          {row.stone_quality ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '110px',
      cell: row => (
        <span style={{ color: row.status === 'Active' ? '#00C853' : '#d32f2f', fontWeight: 600 }}>{row.status}</span>
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <>
          <button
            style={actionStyle}
            onClick={() => handleEdit(row)}
            title="Edit"
            onMouseEnter={() => setHoveredAction({ id: row.id, type: 'edit' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <EditIcon fontSize="small" />
            {hoveredAction.id === row.id && hoveredAction.type === 'edit' && (
              <span style={{ marginLeft: 4, color: '#1B5E20', fontWeight: 600 }}>Edit</span>
            )}
          </button>
          <button
            style={actionStyle}
            onClick={() => handleDelete(row.id, row.stone_type_name)}
            title="Trash"
            onMouseEnter={() => setHoveredAction({ id: row.id, type: 'delete' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <DeleteOutlineIcon fontSize="small" />
            {hoveredAction.id === row.id && hoveredAction.type === 'delete' && (
              <span style={{ marginLeft: 4, color: '#d32f2f', fontWeight: 600 }}>Trash</span>
            )}
          </button>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '140px',
    },
  ];

  return (
    <div style={cardStyle}>
      <style>{`
        .stone-type-table ::-webkit-scrollbar {
          height: 6px;
          background: #f2f2f2;
        }
        .stone-type-table ::-webkit-scrollbar-thumb {
          background: #d3d3d3;
          border-radius: 4px;
        }
        .stone-type-table {
          scrollbar-color: #d3d3d3 #f2f2f2;
          scrollbar-width: thin;
        }
      `}</style>
      <div style={headerStyle}>
        <div style={titleStyle}>Stone Type List</div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button style={newBtnStyle} onClick={handleNew}>
            <AddIcon style={{ fontSize: 20, marginRight: 6, verticalAlign: 'middle' }} />
            New
          </button>
          <div style={searchContainer}>
            <input
              type="text"
              placeholder="Search by name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={searchInput}
            />
            <SearchIcon style={searchIconStyle} />
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filteredStoneTypes}
        progressPending={loading}
        highlightOnHover
        pointerOnHover
        dense
        noHeader
        customStyles={{
          rows: { style: { minHeight: 44 } },
          headCells: { style: { fontWeight: 700, fontSize: 15, color: '#111', background: '#f5f5f5' } },
        }}
        className="stone-type-table"
      />
    </div>
  );
};

export default StoneTypeTable; 